import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { useChanged, useLayer } from '../../components/layers'
import { apiJson } from '../../lib/api'
import { PendingButton, useSlow, inertClass, Failed } from '../../components/pending'
import { parseIsoLocal, todayLocal } from '../timesheets/time'
import { DOW_NAMES, SCOPE_LABEL, WEEKLY_MONDAY, money, payPeriodOf, payPeriodText, plural, rangeLabel } from './format'
import type { PayPeriod, Scope, WageSettings } from './types'
import { CsvLayoutEditor, EMPLOYEE_ID_MISSING, layoutOf, layoutProblem, rowsOf, type Row } from './CsvLayoutEditor'

// The Wages cog (V4 wgCog, §7j.2b): tenant configuration that changes what
// workers are paid. The default minimum rate lives in the Personnel cog
// (§7j.2d) and is shown here read-only so the set line has its source.

interface Props {
  user: any
  settings: WageSettings
  scope: Scope
  today?: string // the tenant's (087), for the current-period line
  onClose: () => void
  onSaved: (msg: string, periodChanged?: boolean) => void
}

const TOP_UP: { key: WageSettings['merged_assessment']; label: string; note: string }[] = [
  { key: 'period', label: 'Across the whole period', note: 'One minimum-wage assessment for the merged rows. The brief’s model: a strong day can carry a weak one.' },
  { key: 'per_day', label: 'Per day, then summed', note: 'Each day is assessed on its own and the top-ups are added. Nobody is ever carried by another day.' },
]

const ALLOC: { key: WageSettings['break_charge']; label: string }[] = [
  { key: 'earliest', label: 'Earliest eligible session' },
  { key: 'pro_rata', label: 'Pro-rata across the day' },
]

export function SettingsModal({ user, settings, scope, today, onClose, onSaved }: Props) {
  const [topUp, setTopUp] = useState(settings.merged_assessment)
  const [alloc, setAlloc] = useState(settings.break_charge)
  const [empId, setEmpId] = useState(settings.employee_id_required)
  const [payMode, setPayMode] = useState<Scope | null>(settings.pay_mode ?? null)
  const [withIssues, setWithIssues] = useState(settings.export_with_issues !== false)
  const catalogue = settings.adapters || []
  const initialOn = catalogue.filter((a) => a.enabled).map((a) => a.key)
  const [adaptersOn, setAdaptersOn] = useState<string[]>(initialOn)
  const pp0 = settings.pay_period || WEEKLY_MONDAY
  const [pp, setPp] = useState<PayPeriod>(pp0)
  const csvFields = settings.csv_fields || []
  const csvLayout0 = settings.csv_columns ?? null
  const [csvRows, setCsvRows] = useState<Row[]>(() => rowsOf(csvFields, csvLayout0))
  const csvProblem = csvFields.length ? layoutProblem(csvRows, empId) : null
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once a value
  // has changed. Nothing closes it while its write is out (h 3984-3992).
  const modalEl = useRef<HTMLDivElement>(null)
  const csvLayout = csvFields.length ? layoutOf(csvFields, csvRows) : null
  // D6.9 (Kerrin, 2026-10-03): an older stored layout that cannot carry the
  // required Employee ID does not stop an unrelated change, as in the API. It
  // is only inherited while neither the requirement nor the layout is touched
  // here; any change to either that leaves the combination invalid is refused.
  const inheritedProblem =
    csvProblem === EMPLOYEE_ID_MISSING && empId === settings.employee_id_required && JSON.stringify(csvLayout) === JSON.stringify(csvLayout0)
  const csvBlocks = !!csvProblem && !inheritedProblem
  const dirty = useChanged({ topUp, alloc, empId, payMode, withIssues, adaptersOn, pp, csvLayout })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: modalEl, modal: true, focusFirst: true, busy })

  const ppDay = today || todayLocal()
  // A fortnight needs to know which week is its first. Switching to
  // fortnightly proposes the current week, shown before anything is saved.
  const setWeeks = (weeks: 1 | 2) =>
    setPp((p) => (weeks === 1 ? { weeks: 1, start_dow: p.start_dow, anchor: null } : { weeks: 2, start_dow: p.start_dow, anchor: p.anchor || payPeriodOf(ppDay, { weeks: 1, start_dow: p.start_dow, anchor: null }).from }))
  const setStartDow = (d: number) => setPp((p) => (p.weeks === 2 && p.anchor ? { ...p, start_dow: d, anchor: payPeriodOf(p.anchor, { weeks: 1, start_dow: d, anchor: null }).from } : { ...p, start_dow: d }))
  const setAnchor = (iso: string) => setPp((p) => (iso ? { weeks: 2, anchor: iso, start_dow: ((parseIsoLocal(iso).getDay() + 6) % 7) + 1 } : { ...p, anchor: null }))
  const ppBad = pp.weeks === 2 && !pp.anchor
  const ppChanged = pp.weeks !== pp0.weeks || pp.start_dow !== pp0.start_dow || (pp.anchor || null) !== (pp0.anchor || null)
  const ppNow = ppBad ? null : payPeriodOf(ppDay, pp)

  const noAdapter = catalogue.length > 0 && adaptersOn.length === 0
  const toggleAdapter = (key: string) => setAdaptersOn((on) => (on.includes(key) ? on.filter((k) => k !== key) : on.concat(key)))

  const save = async () => {
    if (noAdapter || ppBad || csvBlocks) return
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = { pay_mode: payMode, merged_assessment: topUp, break_charge: alloc, employee_id_required: empId, export_with_issues: withIssues }
      // Sent only when changed, so a tenant that never chose keeps "every
      // adapter offered" (and so any adapter added later).
      const same = adaptersOn.length === initialOn.length && adaptersOn.every((k) => initialOn.includes(k))
      if (!same) body.enabled_adapters = adaptersOn
      if (ppChanged) body.pay_period = pp
      // The CSV layout is sent only when it changed, like the adapters.
      if (csvFields.length) {
        const layout = layoutOf(csvFields, csvRows)
        if (JSON.stringify(layout) !== JSON.stringify(csvLayout0)) body.csv_columns = layout
      }
      const r = await apiJson<{ changed: string[]; queued: number; current_period?: { from: string; to: string } }>(user, '/api/wages/settings', { body })
      const periodChanged = (r.changed || []).some((k) => k.startsWith('pay_period_'))
      onSaved(
        !r.changed?.length
          ? 'Wage settings unchanged'
          : 'Wage settings saved' +
              (r.queued ? ` · ${plural(r.queued, 'worker/day', 'worker/days')} queued for recalculation` : '') +
              (periodChanged && r.current_period ? ` · the current pay period is ${rangeLabel(r.current_period.from, r.current_period.to)}` : ''),
        periodChanged
      )
    } catch (e: any) {
      setError(e?.message || 'Could not save the settings')
      setBusy(false)
    }
  }

  const scopeNote =
    scope === 'daily'
      ? 'You are viewing Daily, where each day is always assessed on its own. Switch to By Team or Combined to see this setting change the numbers.'
      : `In view now: ${SCOPE_LABEL[scope]}. Changing this changes the top-ups in the table.`

  const seg = (on: boolean) => (on ? `${wo.seg} ${wo.segOn}` : wo.seg)
  const lbl = `${o.label} ${wo.pmLabel}`

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={modalEl} tabIndex={-1} className={`${o.modal} ${wo.pm} ${wo.cog}`} role="dialog" aria-label="Wage settings">
          <div className={`${o.head} ${wo.pmHead}`}>
            <div className={o.headText}>
              <h2 className={`${o.title} ${wo.pmTitle}`}>Wage settings</h2>
              <p className={`${o.sub} ${wo.pmSub} ${wo.cogSub}`}>
                These change what workers are paid, so they are subscription settings rather than a view preference. Changing one re-reads the wage basis; it never rewrites it.
              </p>
            </div>
          </div>

          <div className={`${wo.cogBody} ${inertClass(busy, slow)}`}>
            <div className={wo.section}>
              <div className={lbl}>When days are merged, assess the minimum</div>
              {TOP_UP.map((t) => (
                <button key={t.key} type="button" className={topUp === t.key ? `${o.choice} ${o.choiceOn} ${wo.optRow} ${wo.optOn}` : `${o.choice} ${wo.optRow}`} aria-pressed={topUp === t.key} onClick={() => setTopUp(t.key)}>
                  <span className={`${o.radio} ${wo.optDot}`} />
                  <span className={wo.optText}>
                    <span className={wo.optLabel}>{t.label}</span>
                    <span className={wo.optNote}>{t.note}</span>
                  </span>
                </button>
              ))}
              <div className={wo.hint}>Applies to By Team and Combined. Daily is always assessed per day.</div>
              <div className={wo.scopeNote}>{scopeNote}</div>
            </div>

            <div className={wo.section}>
              <div className={lbl}>Charge the day’s paid break to</div>
              <div className={wo.segRow}>
                {ALLOC.map((t) => (
                  <button key={t.key} type="button" className={seg(alloc === t.key)} aria-pressed={alloc === t.key} onClick={() => setAlloc(t.key)}>
                    {t.label}
                  </button>
                ))}
              </div>
              <div className={wo.hint}>
                How much paid break a worker gets is set by the break schedule in Timesheets, across the whole recorded day. This only decides which team’s work carries its cost, priced at the higher of their minimum rate and that team’s effective piece rate.
              </div>
            </div>

            <div className={wo.section}>
              <div className={lbl}>Employee ID for payroll export</div>
              <div className={wo.segRow}>
                {[
                  [true, 'Required'],
                  [false, 'Optional'],
                ].map(([k, label]) => (
                  <button key={String(k)} type="button" className={seg(empId === k)} aria-pressed={empId === k} onClick={() => setEmpId(k as boolean)}>
                    {label as string}
                  </button>
                ))}
              </div>
              <div className={wo.hint}>
                PayHero and Xero match people on their employee ID. When it is required, exporting warns about anyone who does not have one instead of sending rows that cannot be matched. A Customer CSV layout must then also include the Employee ID column.
              </div>
            </div>

            <div className={wo.section}>
              <div className={lbl}>Calculation mode you pay on</div>
              <div className={wo.segRow} role="radiogroup" aria-label="Calculation mode you pay on">
                {(['daily', 'team', 'combined'] as Scope[]).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={payMode === k} className={seg(payMode === k)} onClick={() => setPayMode(k)}>
                    {SCOPE_LABEL[k]}
                  </button>
                ))}
              </div>
              <div className={wo.hint}>
                {payMode
                  ? `Wages opens on ${SCOPE_LABEL[payMode]}. The other two stay visible as views.`
                  : 'Not set yet, so Wages opens on Daily. The other two stay visible as views either way.'}{' '}
                Changing it queues no recalculation: every scope is worked out from the same wage basis.
              </div>
            </div>

            <div className={wo.section}>
              <div className={lbl}>Pay period</div>
              <div className={wo.segRow} role="radiogroup" aria-label="Pay period length">
                {([
                  [1, 'Weekly'],
                  [2, 'Fortnightly'],
                ] as [1 | 2, string][]).map(([k, label]) => (
                  <button key={k} type="button" role="radio" aria-checked={pp.weeks === k} className={seg(pp.weeks === k)} onClick={() => setWeeks(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <div className={wo.inlineFields}>
                <label>
                  Starts on
                  <select value={pp.start_dow} onChange={(e) => setStartDow(Number(e.target.value))} aria-label="Pay period starts on">
                    {DOW_NAMES.map((d, i) => (
                      <option key={d} value={i + 1}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
                {pp.weeks === 2 && (
                  <label>
                    One fortnight starts
                    <input type="date" value={pp.anchor || ''} onChange={(e) => setAnchor(e.target.value)} aria-label="One fortnight starts" />
                  </label>
                )}
              </div>
              <div className={wo.hint} data-testid="pay-period-now">
                {ppNow
                  ? `${payPeriodText(pp)}. The current pay period is ${rangeLabel(ppNow.from, ppNow.to)}, and Pay Workers opens on it.`
                  : 'Choose the date one fortnight starts, so the fortnights line up with your pay runs.'}{' '}
                A pay period is a set of work dates. Changing it recalculates nothing; any range can still be picked and exported.
              </div>
            </div>

            <div className={wo.section}>
              <div className={lbl}>Exporting with unresolved issues</div>
              <div className={wo.segRow} role="radiogroup" aria-label="Exporting with unresolved issues">
                {[
                  [true, 'Permitted once acknowledged'],
                  [false, 'Not permitted'],
                ].map(([k, label]) => (
                  <button key={String(k)} type="button" role="radio" aria-checked={withIssues === k} className={seg(withIssues === k)} onClick={() => setWithIssues(k as boolean)}>
                    {label as string}
                  </button>
                ))}
              </div>
              <div className={wo.hint}>
                {withIssues
                  ? 'An issue that still produces a number, such as a missing employee ID or a day not yet recalculated, can be exported once someone acknowledges it. The acknowledgement is recorded against the export.'
                  : 'Every such issue must be resolved, or those workers left out, before payroll can be exported. Nobody can acknowledge past them.'}{' '}
                A wage that cannot be calculated at all is never exported either way.
              </div>
            </div>

            {catalogue.length > 0 && (
              <div className={wo.section}>
                <div className={lbl}>Payroll adapters offered</div>
                <div className={wo.checks} role="group" aria-label="Payroll adapters offered">
                  {catalogue.map((a) => (
                    <label key={a.key} className={wo.checkRow}>
                      <input type="checkbox" checked={adaptersOn.includes(a.key)} onChange={() => toggleAdapter(a.key)} />
                      <span>
                        {a.label} <span className={wo.fmt}>{a.kind === 'api' ? 'API push' : 'File'}</span>
                        {!a.ready && <span className={wo.muted}> · {a.note}</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <div className={wo.hint}>Export payroll lists only these. An adapter that is not ready stays unable to produce a file whether it is ticked or not.</div>
                {noAdapter && <div className={wo.err}>Offer at least one payroll adapter.</div>}
              </div>
            )}

            {csvFields.length > 0 && <CsvLayoutEditor fields={csvFields} rows={csvRows} setRows={setCsvRows} csvOffered={adaptersOn.includes('csv')} employeeIdRequired={empId} inherited={inheritedProblem} />}

            <div className={wo.section}>
              <div className={lbl}>Default minimum rate</div>
              <div className={wo.hint}>
                {settings.default_min_rate != null ? money(settings.default_min_rate) + '/h' : 'Not set'} · set in Personnel settings. Workers added without a minimum rate get this rate.
              </div>
            </div>

            <div className={`${o.warn} ${wo.warnBox}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" /></svg>
              <span>
                Minimum wage, top-up and paid-break rules are set by law, and the law differs by country, state and often by employment agreement. Dataphyll will apply whatever settings you enter. It does not check them against any jurisdiction. Making sure these settings meet your obligations is your responsibility, so take advice if you are not certain.
              </span>
            </div>
          </div>

          <div className={`${o.foot} ${wo.pmFoot}`}>
            <PendingButton type="button" className={`${o.primary} ${wo.pmBtn}`} disabled={noAdapter || ppBad || csvBlocks} busy={busy} pending="Saving…" onClick={save}>
              Save settings
            </PendingButton>
            <button type="button" className={`${o.ghost} ${wo.pmGhost}`} onClick={requestClose} disabled={busy}>
              Cancel
            </button>
            <Failed text={error} />
          </div>
        </div>
      </div>
      {confirm}
    </>
  )
}
